import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ActionIcon, Box, Card, Group, Menu, Stack, Text } from '@mantine/core';
import {
  IconBuildingBank,
  IconCalendar,
  IconCircleMinus,
  IconDots,
  IconFlask,
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
  IconTruckLoading,
} from '@tabler/icons-react';
import SaleStatusBadge from '@/components/app/sell/SaleStatusBadge';
import ExchangeStatusBadge from '@/components/app/sell/ExchangeStatusBadge';
import MetaChip from '@/components/common/MetaChip';
import PrepaymentBadge from '@/components/common/PrepaymentBadge';
import BestPriceLink from '@/components/common/BestPriceLink';
import { bestOfferLink, bestPriceTone } from '@/lib/bestOffer';
import { canPublishToExchange, exchangeUiState, isFrozenByExchange } from '@/lib/exchangeOffer';
import { formatMoney } from '@/lib/format';
import { loadingWindow, mainQuality } from '@/lib/sellOffer';
import type { MyBestOffer } from '@/types/bestPrice';
import type { ExchangeSession, SaleExchangeStatusInfo, SaleOrder } from '@/types/sell';
import classes from './OfferLotCard.module.css';

interface OfferLotCardProps {
  readonly offer: SaleOrder;
  readonly to: string;
  readonly editTo: string;
  readonly onSuspend: () => void;
  readonly onUnsuspend: () => void;
  readonly onDelete: () => void;
  /** Опубликовать биржевой оффер на бирже (доступно в окне торгов). */
  readonly onPublishExchange?: () => void;
  /** Снять биржевой оффер с биржи (с подтверждением). */
  readonly onRemoveExchange?: () => void;
  /** Биржевой статус оффера (для бейджа и выбора действий); отсутствует — не на бирже. */
  readonly exchangeInfo?: SaleExchangeStatusInfo;
  /** Загружен ли биржевой статус (иначе действия/бейдж не показываем как разрешённое состояние). */
  readonly exchangeReady?: boolean;
  /** Торговая сессия биржи (окно + «открыто сейчас») — доступность публикации и подсказка о часах. */
  readonly session?: ExchangeSession;
  /** Лучшее встречное предложение покупателя по этому офферу (если спрос есть) — кликабельная цена. */
  readonly bestOffer?: MyBestOffer;
  /** Идёт действие именно по этому лоту (отключаем меню). */
  readonly pending?: boolean;
}

// Карточка оффера-лота в детали базиса+культуры: качество/предоплата/статус + объём + цена + окно
// погрузки. Клик по карточке → деталь оффера; деструктивные/жизненный цикл — в меню «⋯» (вне зоны
// случайного клика по навигации). Для биржевых офферов вместо
// паузы/возобновления — публикация/снятие с биржи; при живой заявке правка/удаление заморожены.
export default function OfferLotCard({
  offer,
  to,
  editTo,
  onSuspend,
  onUnsuspend,
  onDelete,
  onPublishExchange,
  onRemoveExchange,
  exchangeInfo,
  exchangeReady,
  session,
  bestOffer,
  pending,
}: OfferLotCardProps) {
  const { t } = useTranslation();
  const quality = mainQuality(offer);
  const window = loadingWindow(offer);
  const suspended = offer.status === 'SUSPENDED';
  // Для биржевого оффера, пока не загружен биржевой статус, состояние неизвестно — не показываем
  // действия как разрешённые (иначе замороженная заявка на миг выглядела бы редактируемой).
  const exUnresolved = Boolean(offer.toExchange) && exchangeReady === false;
  const exState = exchangeUiState(offer, exchangeInfo);
  const isExchange = exState !== null;
  const frozen = isFrozenByExchange(exState); // живая заявка: правка/удаление скрыты
  const canPublish = !exUnresolved && canPublishToExchange(exState);
  const editable = !exUnresolved && !frozen && exState !== 'MATCHED';
  const sessionOpen = session?.open;
  const showRemoveExchange = isExchange && frozen;
  // Пока статус биржи не загружен (exUnresolved) или оффер в сделке (MATCHED) — actionable-пунктов нет;
  // тогда меню «⋯» не показываем вовсе, чтобы не открывался пустой список.
  const hasMenuItems = editable || !isExchange || canPublish || showRemoveExchange;

  // Меню не должно срабатывать как переход по карточке-ссылке.
  const stop = (event: React.SyntheticEvent): void => event.stopPropagation();

  // «Растянутая ссылка»: карточка НЕ ссылка (иначе кнопка меню оказалась бы <button> внутри <a> —
  // невалидный HTML). Навигацию даёт прозрачный Link-оверлей (zIndex 1), а меню поднято над ним
  // (zIndex 2) и кликается отдельно. Цена/контент — под оверлеем, клик по ним ведёт на деталь.
  return (
    <Card withBorder padding="md" pos="relative" className={classes.card}>
      <Box
        component={Link}
        to={to}
        aria-label={[offer.goodsType, quality].filter(Boolean).join(' · ') || t('app.sell.lot.actions')}
        style={{ position: 'absolute', inset: 0, zIndex: 1, borderRadius: 'inherit' }}
      />
      {/* wrap: на узком экране блок цены/меню переносится под чипы и объём, а не сжимает их
          (иначе чипы качества/предоплаты схлопываются в нечитаемые квадраты). */}
      <Group justify="space-between" wrap="wrap" gap="sm" align="flex-start">
        <Stack gap={8} style={{ minWidth: 0, flex: '1 1 240px' }}>
          <Group gap={6} wrap="wrap" align="center">
            {/* Таксономия бейджей (design-guidelines): статус — семантический цвет; качество/объём —
                серое метаданное; предоплата — зелёный PrepaymentBadge (важное измерение цены). */}
            <SaleStatusBadge status={offer.status} />
            {quality ? <MetaChip icon={<IconFlask size={13} />}>{quality}</MetaChip> : null}
            {/* Фолбэк type/name: если бэкенд пришлёт тип предоплаты без числового percent, бейдж всё равно
                отрисуется (PrepaymentBadge извлекает число из 'PERCENT_90' и т.п.), а не исчезнет. */}
            <PrepaymentBadge value={offer.prepaymentInfo?.percent ?? offer.prepaymentInfo?.type ?? offer.prepaymentInfo?.name} compact />
            <ExchangeStatusBadge state={exState} loading={exUnresolved} />
          </Group>
          <Group gap="lg" wrap="wrap">
            <Group gap={4} wrap="nowrap">
              <IconTruckLoading size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
              <Text fw={600}>
                {offer.weightTon != null ? `${formatMoney(offer.weightTon)} ${t('app.sell.tons')}` : '—'}
              </Text>
            </Group>
            {window ? (
              <Group gap={4} wrap="nowrap">
                <IconCalendar size={16} color="var(--mantine-color-gray-6)" style={{ flexShrink: 0 }} />
                <Text size="sm" c="gray.8">
                  {window}
                </Text>
              </Group>
            ) : null}
          </Group>
        </Stack>

        <Group gap="sm" wrap="nowrap" align="flex-start" style={{ flexShrink: 0 }}>
          {/* Лучшая встречная цена — на одной линии с моей ценой и ПЕРЕД ней (слева). */}
          <Group gap="md" wrap="nowrap" align="center">
            {bestOffer?.bestPrice != null ? (
              <BestPriceLink
                to={bestOfferLink(bestOffer)}
                price={bestOffer.bestPrice}
                action="sell"
                tone={bestPriceTone(bestOffer.advantage)}
                advantage={bestOffer.advantage}
              />
            ) : null}
            <Stack gap={0} align="flex-end">
              <Text fw={700} fz={20} c="brand.9">
                {offer.price != null ? formatMoney(offer.price) : '—'}
              </Text>
              <Text size="xs" c="gray.7">
                {t('app.sell.rubPerTon')}
              </Text>
            </Stack>
          </Group>
          {hasMenuItems ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="default"
                size={34}
                aria-label={t('app.sell.lot.actions')}
                loading={pending}
                // Поднято над ссылкой-оверлеем, чтобы клик открывал меню, а не вёл на деталь.
                style={{ position: 'relative', zIndex: 2 }}
                onClick={stop}
              >
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown onClick={stop}>
              {editable ? (
                <Menu.Item component={Link} to={editTo} leftSection={<IconPencil size={16} />}>
                  {t('app.sell.lot.edit')}
                </Menu.Item>
              ) : null}

              {!isExchange && suspended ? (
                <Menu.Item
                  leftSection={<IconPlayerPlay size={16} />}
                  onClick={(event) => {
                    event.preventDefault();
                    onUnsuspend();
                  }}
                >
                  {t('app.sell.lot.resume')}
                </Menu.Item>
              ) : null}
              {!isExchange && !suspended ? (
                <Menu.Item
                  leftSection={<IconPlayerPause size={16} />}
                  onClick={(event) => {
                    event.preventDefault();
                    onSuspend();
                  }}
                >
                  {t('app.sell.lot.suspend')}
                </Menu.Item>
              ) : null}

              {isExchange && canPublish ? (
                <Menu.Item
                  leftSection={<IconBuildingBank size={16} />}
                  disabled={!sessionOpen}
                  onClick={(event) => {
                    event.preventDefault();
                    onPublishExchange?.();
                  }}
                >
                  {t(exState === 'FAILED' ? 'app.sell.exchange.republish' : 'app.sell.exchange.publish')}
                </Menu.Item>
              ) : null}
              {isExchange && canPublish && session && !session.open ? (
                <Menu.Label>
                  {t('app.sell.exchange.windowClosedHint', { start: session.windowStart, end: session.windowEnd })}
                </Menu.Label>
              ) : null}
              {isExchange && frozen ? (
                <Menu.Item
                  color="red"
                  leftSection={<IconCircleMinus size={16} />}
                  onClick={(event) => {
                    event.preventDefault();
                    onRemoveExchange?.();
                  }}
                >
                  {t('app.sell.exchange.removeAction')}
                </Menu.Item>
              ) : null}

              {editable ? (
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={(event) => {
                    event.preventDefault();
                    onDelete();
                  }}
                >
                  {t('app.sell.lot.delete')}
                </Menu.Item>
              ) : null}
            </Menu.Dropdown>
          </Menu>
          ) : null}
        </Group>
      </Group>
    </Card>
  );
}
